/**
 * TRAD-3847 — 팀 기기·QA 트래픽을 GA 에서 가를 수 있게 user property `internal_user` 를 붙인다.
 *
 * GA 409625670 의 10/19~25 한국 iOS 6명이 참여시간 ≈96h·sign_up 12건(2명)이었다 — 팀 테스트로 추정되고,
 * 그대로면 8주 실험(TRAD-3707) 12/7 점검에서 외부 유저 사용량을 못 본다. 수집을 끄지 않고 «표시»만 한다
 * (GA 보고서 쪽 제외 필터는 davidyoon 몫 — 이 속성을 맞춤 측정기준으로 등록해야 걸 수 있다).
 *
 * 🔴 이름·값은 iOS·Android 와 같아야 한다: `internal_user` = `"true"` / `"false"`(문자열). 플랫폼마다 다르면 필터가 한쪽만 걸린다.
 *
 * ── true 조건 ─────────────────────────────────────────────────────────────────
 *  - 운영 호스트(xtrading.kr·www)가 아닌 곳에서 열었다 — dev.xtrading.kr·localhost·IP 직결 등
 *  - 서버 로그인·재발급 응답의 `internalUser` 가 true 였다 — `recordServerInternalUser` 가 sticky 로 남긴다(아래)
 *  - 이 브라우저가 전에 한 번이라도 true 였다(sticky — QA 계정이 로그아웃해도 같은 기기는 계속 내부)
 * 그 밖은 전부 false 다. 🔴 필드 없음·판정 실패는 false — 외부 유저를 내부로 잘못 빼면 그 사람이 지표에서 사라진다.
 *
 * ── 🔴 이 판정이 못 잡는 것 ───────────────────────────────────────────────────
 *  - 팀원이 운영(xtrading.kr)을 비로그인으로, 한 번도 내부로 판정된 적 없는 브라우저에서 연 경우 → false.
 *  - sticky 는 localStorage 라 «출처(origin)별»이다. dev.xtrading.kr 에서 true 가 돼도 xtrading.kr 에는 안 옮겨 간다.
 *  - 저장소가 막힌 브라우저(사생활 보호 모드 등)는 sticky 없이 그때그때의 신호만 본다.
 */

import { resolveStorage, type StorageLike } from '@/shared/lib/cookie-consent';

/** GA user property 이름 — iOS·Android 와 같은 이름. */
export const INTERNAL_USER_PROPERTY = 'internal_user';

/** sticky 를 담는 localStorage 키. 값은 `'true'` 하나만 쓴다(false 는 저장하지 않는다 — «아직 모름»과 같다). */
export const INTERNAL_USER_STORAGE_KEY = 'ga-internal-user';

/** 운영 호스트. 여기 없는 호스트는 전부 내부(dev·로컬·IP 직결)로 본다. */
const PRODUCTION_HOSTS: ReadonlySet<string> = new Set(['xtrading.kr', 'www.xtrading.kr']);

/**
 * 순수 함수 — 운영 호스트가 아니면 true. 대소문자·끝의 점(`xtrading.kr.`)은 같은 호스트로 본다.
 * 빈 호스트(판정 불가)는 false 다 — 위 «판정 실패는 false» 규칙.
 */
export const isInternalHost = (hostname: string): boolean => {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  return host !== '' && !PRODUCTION_HOSTS.has(host);
};

export type InternalUserSignals = {
  hostname: string;
  /** 이 브라우저에 저장된 sticky — 서버 `internalUser=true` 도 여기로 들어온다(`recordServerInternalUser`). */
  stored: boolean;
};

/** 순수 함수 — 둘 중 하나라도 true 면 내부. */
export const decideInternalUser = ({ hostname, stored }: InternalUserSignals): boolean =>
  stored || isInternalHost(hostname);

/** GA 에 보낼 값 — 문자열. */
export const toInternalUserPropertyValue = (internal: boolean): 'true' | 'false' => (internal ? 'true' : 'false');

/** 주입된 저장소에서 sticky 를 읽는다. 읽을 수 없으면 false(= 아직 모름). */
export const readInternalUserFrom = (storage: StorageLike | null): boolean => {
  if (!storage) return false;
  try {
    return storage.getItem(INTERNAL_USER_STORAGE_KEY) === 'true';
  } catch (e) {
    console.error('[internal-user] failed to read sticky flag', e);
    return false;
  }
};

/** 주입된 저장소에 sticky 를 남긴다. 던지지 않는다 — 실패해도 이번 방문의 판정은 그대로다. */
export const persistInternalUserTo = (storage: StorageLike | null): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(INTERNAL_USER_STORAGE_KEY, 'true');
    return true;
  } catch (e) {
    console.error('[internal-user] failed to persist sticky flag', e);
    return false;
  }
};

/**
 * 지금 이 방문자가 내부인가 — 공개 API(전역 location·localStorage 를 쓴다).
 * true 로 판정되면 sticky 를 남긴다(이미 있으면 다시 쓰지 않는다).
 */
export const resolveInternalUser = (): boolean => {
  const storage = resolveStorage();
  const stored = readInternalUserFrom(storage);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const internal = decideInternalUser({ hostname, stored });
  if (internal && !stored) persistInternalUserTo(storage);
  return internal;
};

/** 서버 신호로 sticky 가 새로 켜졌을 때 발화 — 이미 초기화된 Analytics 가 속성을 다시 붙인다(firebase.ts). */
export const INTERNAL_USER_CHANGED_EVENT = 'ga-internal-user:changed';

/**
 * 서버 응답의 `internalUser` 를 기록한다 — 로그인(`result.userRes.internalUser`: /auth/login · google/verify · apple/verify)과
 * 토큰 재발급(`result.internalUser`: /auth/reissue-access-token) 응답이 지나가는 곳에서 부른다.
 *
 * - `=== true` 일 때만 sticky 를 켜고 이벤트를 쏜다.
 * - false·없음·계약 밖 값은 아무것도 안 한다 — 🔴 **이미 켜진 true 를 끄지 않는다**(팀장 2026-10-26: false 가 와도 기존 true 유지).
 *   QA 계정에서 로그아웃하고 개인 계정으로 들어와도 같은 기기는 내부로 남는다.
 * 던지지 않는다 — 로그인·재발급 흐름이 계측 때문에 깨지면 안 된다.
 */
export const recordServerInternalUser = (flag: unknown): void => {
  if (flag !== true) return;
  try {
    persistInternalUserTo(resolveStorage());
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(INTERNAL_USER_CHANGED_EVENT));
    }
  } catch (e) {
    console.error('[internal-user] failed to record server internalUser', e);
  }
};
